/**
 * King Protein — Cart features (Dawn-native)
 *
 * The cart drawer, cart-count bubble, and is-empty state are all owned by Dawn's
 * Section Rendering engine (cart.js + cart-drawer.js + product-form.js renderContents).
 * The Liquid Ajax Cart request-end listener, the manual cart-count-bubble toggle, the
 * manual is-empty toggle on <cart-drawer>, and the drawer-open-on-add were all removed
 * in Phase 25 (plans 25.01/25.02/25.03): Dawn re-renders the cart-drawer + cart-icon-bubble
 * sections on every add/quantity/remove, so those manual DOM toggles are redundant.
 *
 * What remains here: the cart upsell fetch (re-triggered via Dawn's cartUpdate pub/sub),
 * the discount-code accordion, and the checkout discount intercept.
 */

/**
 * King Protein — Cart Upsells
 * Fetches complementary product recommendations for the first cart item.
 * Uses Shopify Recommendations API with section_id for server-rendered HTML.
 *
 * Triggered on initial load (x-init in snippets/kp-cart-upsells.liquid) and re-triggered
 * on every cart change via subscribe(PUB_SUB_EVENTS.cartUpdate, …) registered below.
 *
 * @param {HTMLElement} el - the .kp-cart-upsells Alpine root.
 * @param {Object} [cartData] - the cart payload from a Dawn cartUpdate event. When absent
 *   (initial load / direct call), the cart is fetched from the cart URL first.
 */
function fetchUpsells(el, cartData) {
  if (!el) return;

  if (cartData && cartData.items) {
    renderUpsellsForCart(el, cartData);
    return;
  }

  /* No cart payload supplied (initial load) — fetch the current cart first. */
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  fetch(root + 'cart.js')
    .then(function (response) {
      if (!response.ok) throw new Error('Cart fetch failed');
      return response.json();
    })
    .then(function (cart) {
      renderUpsellsForCart(el, cart);
    })
    .catch(function () {
      if (el._x_dataStack && el._x_dataStack[0]) el._x_dataStack[0].loaded = false;
    });
}

/**
 * Renders upsell recommendation cards into the upsell container for a given cart object.
 *
 * @param {HTMLElement} el - the .kp-cart-upsells Alpine root.
 * @param {Object} cart - a cart object (cartData from cartUpdate, or a fetched /cart.js cart).
 */
function renderUpsellsForCart(el, cart) {
  if (!cart || !cart.items || cart.items.length === 0) {
    if (el._x_dataStack && el._x_dataStack[0]) el._x_dataStack[0].loaded = false;
    return;
  }

  const productId = cart.items[0].product_id;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const url = root + 'recommendations/products?product_id=' + productId + '&limit=4&intent=complementary&section_id=kp-cart-upsells';

  fetch(url)
    .then(function(response) {
      if (!response.ok) throw new Error('Recommendations fetch failed');
      return response.text();
    })
    .then(function(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('.kp-cart-upsell-card');
      const container = el.querySelector('#kp-cart-upsells-container');

      if (cards.length > 0 && container) {
        container.innerHTML = '';
        let insertedCount = 0;

        cards.forEach(function(card) {
          if (insertedCount >= 2) return;
          container.appendChild(card.cloneNode(true));
          insertedCount++;
        });

        if (insertedCount > 0) {
          Alpine.nextTick(function() {
            if (el._x_dataStack && el._x_dataStack[0]) el._x_dataStack[0].loaded = true;
          });
        } else {
          if (el._x_dataStack && el._x_dataStack[0]) el._x_dataStack[0].loaded = false;
        }
      } else {
        if (el._x_dataStack && el._x_dataStack[0]) el._x_dataStack[0].loaded = false;
      }
    })
    .catch(function() {
      if (el._x_dataStack && el._x_dataStack[0]) el._x_dataStack[0].loaded = false;
    });
}

document.addEventListener('alpine:init', () => {
  /**
   * King Protein — Cart upsell refresh (Dawn pub/sub)
   * Re-fetch the upsell recommendations whenever the cart changes. Every add-to-cart
   * path (PDP, sticky ATC, upsell, quick-add) routes through Dawn's <product-form>,
   * which publishes PUB_SUB_EVENTS.cartUpdate with { source, productVariantId, cartData }.
   * This replaces the old Liquid Ajax Cart `request-end` listener.
   *
   * Registered inside alpine:init (fires after the deferred constants.js/pubsub.js have
   * executed) and guarded so it no-ops gracefully if the pub/sub globals are unavailable —
   * the same subscribe() guard pattern proven theme-wide by kp-sticky-atc.liquid.
   */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      fetchUpsells(document.querySelector('.kp-cart-upsells'), event && event.cartData);
    });
  }

  /**
   * King Protein — Discount Code Accordion
   * Stores the discount code in sessionStorage and appends it to the checkout URL
   * as ?discount=CODE. Shopify's Storefront Cart API does not support discount
   * validation or application — discounts are only calculated at checkout.
   * Uses x-text for all dynamic text (never x-html) per security model.
   */
  Alpine.data('kpDiscountCode', () => ({
    code: '',
    expanded: false,
    status: '',      // '' | 'applied' | 'error'
    infoText: '',
    errorText: '',

    init() {
      const saved = sessionStorage.getItem('kp_discount_code');
      if (saved) {
        this.code = saved;
        this.status = 'applied';
        const template = this.$el.dataset.infoMsg || this.$el.dataset.successTemplate || 'Code [CODE] will be applied at checkout';
        this.infoText = template.replace('[CODE]', saved);
      }
    },

    applyCode() {
      const trimmed = this.code.trim();
      if (!trimmed) return;

      // Basic format validation: alphanumeric, dashes, underscores only
      if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) {
        this.status = 'error';
        this.errorText = this.$el.dataset.invalidMsg || 'Invalid discount code';
        return;
      }

      sessionStorage.setItem('kp_discount_code', trimmed);
      this.status = 'applied';
      const template = this.$el.dataset.infoMsg || this.$el.dataset.successTemplate || 'Code [CODE] will be applied at checkout';
      this.infoText = template.replace('[CODE]', trimmed);
      this.errorText = '';
    },

    removeCode() {
      sessionStorage.removeItem('kp_discount_code');
      this.code = '';
      this.status = '';
      this.infoText = '';
      this.errorText = '';
    }
  }));

  /**
   * Intercept checkout form submission to append discount code from sessionStorage.
   * The checkout button is a submit button for CartDrawer-Form with action="/cart".
   * Shopify processes ?discount=CODE on the /checkout URL.
   */
  const checkoutBtn = document.getElementById('CartDrawer-Checkout');
  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', function(e) {
      const discount = sessionStorage.getItem('kp_discount_code');
      if (discount) {
        e.preventDefault();
        const root = window.Shopify?.routes?.root || '/';
        window.location.href = root + 'checkout?discount=' + encodeURIComponent(discount);
      }
    });
  }
});
